import type { MetaFunction } from "@remix-run/cloudflare";
import { useEffect, useState } from "react";
import AppAuthGate, { isAppAuthenticated } from "~/components/AppAuthGate";
import { SplashScreen } from "~/components/SplashScreen";
import styles from "~/styles/brubri.module.css";

export const meta: MetaFunction = () => [
  { title: "Brubri — Prodigium Dynamics" },
  { name: "description", content: "A task execution system built for neurodivergent minds." },
];

type Screen = "onboarding" | "dashboard" | "focus" | "stats" | "profile";

const OB_OPTIONS = [
  [
    { title: "TDAH — INATENTO", desc: "Me pierdo en los detalles, olvido tareas a la mitad, me cuesta arrancar." },
    { title: "TDAH — HIPERACTIVO/IMPULSIVO", desc: "Actúo antes de pensar, cambio de tarea constantemente, necesito movimiento." },
    { title: "TDAH COMBINADO / AuDHD", desc: "Ambos patrones. A veces hiperfoco, a veces parálisis total." },
    { title: "NO SÉ / NO TENGO DIAGNÓSTICO", desc: "Reconozco los patrones pero no tengo papel." },
  ],
  [
    { title: "AL ARRANCAR", desc: "Sé lo que tengo que hacer pero no puedo empezar." },
    { title: "A LA MITAD", desc: "Empiezo bien pero pierdo el hilo, me distraigo o abandono." },
    { title: "AMBOS DEPENDIENDO DEL DÍA", desc: "Inconsistente. Mi cerebro no funciona igual todos los días." },
  ],
  [
    { title: "PICOS Y CAÍDAS", desc: "Tengo momentos de energía alta y crashes totales sin aviso." },
    { title: "CONSISTENTEMENTE BAJA", desc: "Rara vez siento que tengo energía suficiente." },
    { title: "DEPENDE DE SI HAY INTERÉS", desc: "Hiperfoco en lo que me gusta, parálisis en lo que no." },
  ],
];

function generateHeatmap() {
  const levels = ["", "l1", "l2", "l3", "l4"];
  return Array.from({ length: 52 }, (_, col) =>
    Array.from({ length: 7 }, (_, row) => ({
      level: levels[Math.floor(Math.random() * levels.length)],
      today: col === 51 && row === new Date().getDay(),
    }))
  );
}

export default function BrubriPage() {
  const [authReady, setAuthReady] = useState(() => isAppAuthenticated("BRUBRI"));

  const [screen, setScreen] = useState<Screen>("onboarding");
  const [obStep, setObStep] = useState(1);
  const [selected, setSelected] = useState<Record<number, number>>({});
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [heatmap, setHeatmap] = useState<ReturnType<typeof generateHeatmap>>([]);

  useEffect(() => {
    setHeatmap(generateHeatmap());
  }, []);
  const [activeNav, setActiveNav] = useState(0);
  const [focusStep] = useState(1);

  const showScreen = (s: Screen, nav?: number) => {
    setScreen(s);
    if (nav !== undefined) setActiveNav(nav);
  };

  const navItems = [
    { icon: "◈", label: "INICIO", screen: "dashboard" as Screen },
    { icon: "⚡", label: "FOCO", screen: "focus" as Screen },
    { icon: "◉", label: "STATS", screen: "stats" as Screen },
    { icon: "○", label: "PERFIL", screen: "profile" as Screen },
  ];

  if (!authReady) {
    return <AppAuthGate appName="BRUBRI" appTag="Cognitive OS" onAuth={() => setAuthReady(true)} />;
  }

  return (
    <>
      <SplashScreen name="BRUBRI" tag="Cognitive OS" />
    <div className={styles.appRoot}>
      <nav className={styles.appNav}>
        <div className={styles.navLogo}>BRUBRI<span>by PRODIGIUM</span></div>
        <div className={styles.navRight}>
          <div className={styles.statusDot} />
          <span className={styles.navStatus}>SISTEMA ACTIVO</span>
          <button className={styles.btnNav} onClick={() => showScreen("dashboard", 0)}>ABRIR APP</button>
        </div>
      </nav>

      <div className={styles.app}>

        {screen === "onboarding" && (
          <div className={`${styles.screen} ${styles.screenActive}`}>
            {[1, 2, 3].map((step) => (
              obStep === step && (
                <div key={step} className={styles.obStep}>
                  <div className={styles.obProgress}>
                    {[1, 2, 3, 4].map((dot) => (
                      <div key={dot} className={`${styles.obProgressDot} ${dot < step ? styles.obDotDone : ""} ${dot === step ? styles.obDotCurrent : ""}`} />
                    ))}
                  </div>
                  <p className={styles.obEyebrow}>CALIBRACIÓN INICIAL // 0{step} DE 04</p>
                  <h1 className={styles.obTitle}>
                    {step === 1 && <>¿Qué describe mejor tu <em>perfil</em>?</>}
                    {step === 2 && <>¿Dónde se <em>rompe</em> tu ejecución?</>}
                    {step === 3 && <>¿Cómo es tu <em>energía</em> típica durante el día?</>}
                  </h1>
                  <p className={styles.obSubtitle}>
                    {step === 1 && "No es diagnóstico. Es calibración. Cuanto más preciso seas, más afilado será tu modo."}
                    {step === 2 && "El momento exacto en que tu cerebro dice no."}
                    {step === 3 && "Honesto. No cómo quisieras que fuera."}
                  </p>
                  <div className={styles.obOptions}>
                    {OB_OPTIONS[step - 1].map((opt, i) => (
                      <div key={i} className={`${styles.obOption} ${selected[step] === i ? styles.obOptionSelected : ""}`} onClick={() => setSelected((p) => ({ ...p, [step]: i }))}>
                        <div className={styles.obOptionCheck} />
                        <div className={styles.obOptionText}>
                          <strong>{opt.title}</strong>
                          <span>{opt.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button className={styles.obBtn} onClick={() => setObStep(step + 1)}>CONTINUAR →</button>
                  {step > 1 && <button className={styles.obBtnGhost} onClick={() => setObStep(step - 1)}>← VOLVER</button>}
                </div>
              )
            ))}

            {obStep === 4 && (
              <div className={styles.obStep}>
                <div className={styles.obProgress}>
                  {[1, 2, 3, 4].map((dot) => (
                    <div key={dot} className={`${styles.obProgressDot} ${dot < 4 ? styles.obDotDone : styles.obDotCurrent}`} />
                  ))}
                </div>
                <p className={styles.obEyebrow}>CALIBRACIÓN COMPLETA // RESULTADO</p>
                <h1 className={styles.obTitle}>Tu modo <em>recomendado</em></h1>
                <p className={styles.obSubtitle}>Basado en tu perfil. Puedes cambiarlo en cualquier momento.</p>
                <div className={styles.obRecommendation}>
                  <p className={styles.obRecLabel}>MODO ASIGNADO</p>
                  <p className={styles.obRecMode}>MICROTASK + 3-2-1 LAUNCH</p>
                  <p className={styles.obRecDesc}>Tu patrón dominante es parálisis de inicio con energía inconsistente. Este modo fragmenta cada objetivo al límite mínimo ejecutable y usa un trigger de arranque externo.</p>
                  <div className={styles.obRecTags}>
                    {["PARÁLISIS DE INICIO", "AuDHD", "ENERGÍA VARIABLE"].map((t) => <span key={t} className={styles.obRecTag}>{t}</span>)}
                  </div>
                </div>
                <button className={styles.obBtn} onClick={() => showScreen("dashboard", 0)}>ACTIVAR SISTEMA →</button>
                <button className={styles.obBtnGhost} onClick={() => setObStep(3)}>← REVISAR</button>
              </div>
            )}
          </div>
        )}

        {screen === "dashboard" && (
          <div className={`${styles.screen} ${styles.screenActive}`}>
            <div className={styles.dashHeader}>
              <p className={styles.dashGreeting}>BIENVENIDO DE VUELTA //</p>
              <p className={styles.dashName}>USUARIO</p>
              <div className={styles.dashModeBadge}><span>◈</span><span>MODO: MICROTASK + 3-2-1</span></div>
            </div>

            <p className={styles.sectionLabel}>ESTADO NEURAL HOY</p>
            <div className={styles.neuroPanel}>
              <div className={styles.neuroPanelHeader}>
                <span className={styles.neuroStateLabel}>◈ NEUROSALUD</span>
                <span className={styles.neuroStateTime}>ÚLTIMA ACTUALIZACIÓN: HACE 2H</span>
              </div>
              <div className={styles.neuroMetrics}>
                {[
                  { label: "RACHA ACTIVA", value: "7", sub: "días consecutivos", color: "green" },
                  { label: "FOCO HOY", value: "1h 23m", sub: "tiempo en ejecución", color: "" },
                  { label: "TAREAS COMPLETADAS", value: "4", sub: "de 6 hoy", color: "green" },
                  { label: "MODO DOMINANTE", value: "MICRO\nTASK", sub: "esta semana", color: "amber" },
                ].map(({ label, value, sub, color }) => (
                  <div key={label} className={styles.neuroMetric}>
                    <p className={styles.neuroMetricLabel}>{label}</p>
                    <p className={`${styles.neuroMetricValue} ${color === "green" ? styles.green : color === "amber" ? styles.amber : ""}`}>{value}</p>
                    <p className={styles.neuroMetricSub}>{sub}</p>
                  </div>
                ))}
              </div>
              <div className={styles.cargaBarWrap}>
                <div className={styles.cargaBarHeader}>
                  <span className={styles.cargaBarLabel}>CARGA COGNITIVA ESTIMADA</span>
                  <span className={styles.cargaBarValue}>MEDIA</span>
                </div>
                <div className={styles.cargaBar}><div className={styles.cargaBarFill} /></div>
              </div>
              <div className={styles.momentumRow}>
                <span className={styles.momentumArrow}>↑</span>
                <span className={styles.momentumText}>Momentum <strong>subiendo</strong> — llevas 3 días mejorando tu racha</span>
              </div>
              <button className={styles.checkinBtn}>+ CHECK-IN MANUAL DE ESTADO</button>
            </div>

            <p className={styles.sectionLabel}>TAREA EN CURSO</p>
            <div className={styles.taskActiveCard}>
              <span className={styles.taskModeChip}>MICROTASK MODE</span>
              <p className={styles.taskActiveTitle}>Terminar el informe mensual de ventas</p>
              <div className={styles.taskProgressWrap}>
                <div className={styles.taskProgressHeader}><span>PROGRESO</span><span>1 / 3 completados</span></div>
                <div className={styles.taskProgressBar}><div className={styles.taskProgressFill} /></div>
              </div>
              <div className={styles.taskSteps}>
                {[
                  { n: "01", text: "Abrir el archivo del mes anterior como referencia", done: true },
                  { n: "02", text: "Copiar la estructura base y borrar los datos viejos", current: true },
                  { n: "03", text: "Llenar solo la sección de resumen ejecutivo" },
                ].map((step) => (
                  <div key={step.n} className={`${styles.taskStep} ${step.done ? styles.taskStepDone : ""} ${step.current ? styles.taskStepCurrent : ""}`}>
                    <span className={styles.taskStepNum}>{step.n}</span>
                    <span className={styles.taskStepText}>{step.text}</span>
                    <div className={styles.taskStepCheck}>{step.done && "✓"}</div>
                  </div>
                ))}
              </div>
              <button className={styles.btnPrimary} onClick={() => showScreen("focus", 1)}>ENTRAR EN MODO FOCO →</button>
            </div>

            <p className={styles.sectionLabel}>ACTIVIDAD DIARIA</p>
            <div className={styles.heatmapWidget} onClick={() => showScreen("stats", 2)}>
              <div className={styles.heatmapHeader}>
                <span className={styles.heatmapTitle}>◈ MAPA DE EJECUCIÓN</span>
                <span className={styles.heatmapLink}>VER STATS →</span>
              </div>
              <div className={styles.heatmapGrid}>
                {heatmap.map((col, ci) => (
                  <div key={ci} className={styles.heatmapCol}>
                    {col.map((cell, ri) => (
                      <div key={ri} className={`${styles.heatmapCell} ${cell.level ? styles[cell.level as keyof typeof styles] : ""} ${cell.today ? styles.today : ""}`} />
                    ))}
                  </div>
                ))}
              </div>
              <div className={styles.heatmapLegend}>
                <span className={styles.heatmapLegendLabel}>MENOS</span>
                {["", "l1", "l2", "l3", "l4"].map((l) => <div key={l} className={`${styles.heatmapLegendCell} ${l ? styles[l as keyof typeof styles] : ""}`} />)}
                <span className={styles.heatmapLegendLabel}>MÁS</span>
              </div>
            </div>

            <div className={styles.newTaskCard} onClick={() => setNewTaskOpen(true)}>
              <div className={styles.newTaskIcon}>+</div>
              <div className={styles.newTaskText}>
                <strong>NUEVA TAREA</strong>
                <span>Describe qué necesitas ejecutar y el sistema lo fragmenta</span>
              </div>
            </div>
          </div>
        )}

        {screen === "focus" && (
          <div className={styles.focusScreen}>
            <div className={styles.focusTop}>
              <button className={styles.focusBack} onClick={() => showScreen("dashboard", 0)}>← VOLVER</button>
              <span className={styles.focusTimer}>00:00:00</span>
              <span className={styles.focusModeLabel}>MICROTASK MODE</span>
            </div>
            <div className={styles.focusCenter}>
              <p className={styles.focusStepCounter}>PASO {focusStep} DE 3</p>
              <p className={styles.focusTaskText}>Copiar la estructura base y borrar los datos viejos</p>
              <div className={styles.focusRingWrap}>
                <svg className={styles.focusRingSvg} width="120" height="120" viewBox="0 0 120 120">
                  <circle className={styles.focusRingBg} cx="60" cy="60" r="45" />
                  <circle className={styles.focusRingFill} cx="60" cy="60" r="45" />
                </svg>
                <div className={styles.focusRingCenter}>
                  <span className={styles.focusRingPct}>33%</span>
                  <span className={styles.focusRingLabel}>PROGRESO</span>
                </div>
              </div>
            </div>
            <button className={styles.focusBtnDone} onClick={() => showScreen("dashboard", 0)}>TAREA COMPLETADA ✓</button>
            <button className={styles.focusBtnStuck}>ESTOY BLOQUEADO — AYUDA</button>
          </div>
        )}

        {screen === "stats" && (
          <div className={`${styles.screen} ${styles.screenActive}`}>
            <p className={styles.sectionLabel}>ESTADÍSTICAS</p>
            <div className={styles.statsPeriodTabs}>
              {["HOY", "SEMANA", "MES", "TODO"].map((t, i) => (
                <button key={t} className={`${styles.statsTab} ${i === 0 ? styles.statsTabActive : ""}`}>{t}</button>
              ))}
            </div>
            <div className={styles.statsKpiRow}>
              {[{ v: "4", label: "Tareas completadas", color: "green" }, { v: "7", label: "Racha actual", color: "" }, { v: "1h23m", label: "Tiempo en foco", color: "amber" }].map(({ v, label, color }) => (
                <div key={label} className={styles.statsKpi}>
                  <span className={`${styles.statsKpiVal} ${color === "green" ? styles.green : color === "amber" ? styles.amber : ""}`}>{v}</span>
                  <span className={styles.statsKpiLabel}>{label}</span>
                </div>
              ))}
            </div>
            <div className={styles.statsChartCard}>
              <p className={styles.statsChartTitle}>ACTIVIDAD SEMANAL</p>
              <div className={styles.barChart}>
                {["L", "M", "X", "J", "V", "S", "D"].map((d, i) => {
                  const heights = [40, 70, 55, 80, 65, 30, 90];
                  const isToday = i === 6;
                  return (
                    <div key={d} className={`${styles.barCol} ${isToday ? styles.barColToday : ""}`}>
                      <span className={styles.barValue}>{heights[i]}%</span>
                      <div className={styles.barFill} style={{ height: `${heights[i]}%` }} />
                      <span className={styles.barLabel}>{d}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.statsChartCard}>
              <p className={styles.statsChartTitle}>MODOS USADOS</p>
              <div className={styles.modeBreakdown}>
                {[{ name: "Microtask", pct: 65 }, { name: "3-2-1 Launch", pct: 20 }, { name: "Hyperfocus", pct: 10 }, { name: "Recovery", pct: 5 }].map(({ name, pct }) => (
                  <div key={name} className={styles.modeRow}>
                    <span className={styles.modeRowName}>{name}</span>
                    <div className={styles.modeRowBar}><div className={styles.modeRowFill} style={{ width: `${pct}%` }} /></div>
                    <span className={styles.modeRowPct}>{pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {screen === "profile" && (
          <div className={`${styles.screen} ${styles.screenActive}`}>
            <div className={styles.profileAvatarWrap}>
              <div className={styles.profileAvatar}>U</div>
              <p className={styles.profileUsername}>USUARIO</p>
              <p className={styles.profileSince}>Activo desde Jun 2026</p>
              <div className={`${styles.profilePlanBadge} ${styles.profilePlanFree}`}>FREE PLAN</div>
            </div>
            <div className={styles.profileSection}>
              <p className={styles.profileSectionTitle}>PERFIL COGNITIVO</p>
              <div className={styles.cognitivoCard}>
                <button className={styles.cognitivoEditBtn}>EDITAR</button>
                {[{ label: "Tipo de perfil", value: "TDAH Combinado / AuDHD" }, { label: "Patrón de bloqueo", value: "Al arrancar + A la mitad" }, { label: "Energía típica", value: "Picos y caídas" }].map(({ label, value }) => (
                  <div key={label} className={styles.cognitivoRow}>
                    <span className={styles.cognitivoLabel}>{label}</span>
                    <span className={`${styles.cognitivoValue} ${styles.cognitivoAccent}`}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.profileSection}>
              <p className={styles.profileSectionTitle}>MODO ACTIVO</p>
              <div className={styles.modeSelectorList}>
                {["MICROTASK + 3-2-1 LAUNCH", "HYPERFOCUS MODE", "RECOVERY MODE", "CUSTOM"].map((mode, i) => (
                  <div key={mode} className={`${styles.modeSelectorItem} ${i === 0 ? styles.modeSelectorItemActive : ""}`}>
                    <div className={`${styles.modeSelectorDot} ${i === 0 ? styles.modeSelectorDotActive : ""}`} />
                    <div className={styles.modeSelectorInfo}><p className={styles.modeSelectorName}>{mode}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.upgradeCta}>
              <p className={styles.upgradeCtaTitle}>Desbloquea todo Brubri</p>
              <p className={styles.upgradeCtaDesc}>Historial ilimitado, modos avanzados, sincronización y análisis cognitivo profundo.</p>
              <p className={styles.upgradePrice}>$4.99 <span>/mes</span></p>
              <button className={styles.obBtn}>UPGRADE A PRO →</button>
            </div>
          </div>
        )}

      </div>

      {screen !== "focus" && (
        <nav className={styles.bottomNav}>
          {navItems.map(({ icon, label, screen: s }, i) => (
            <button key={label} className={`${styles.bottomNavItem} ${activeNav === i ? styles.bottomNavItemActive : ""}`} onClick={() => showScreen(s, i)}>
              <span className={styles.bottomNavIcon}>{icon}</span>
              <span className={styles.bottomNavLabel}>{label}</span>
            </button>
          ))}
        </nav>
      )}

      {newTaskOpen && (
        <div className={styles.newTaskOverlay} onClick={() => setNewTaskOpen(false)}>
          <div className={styles.newTaskSheet} onClick={(e) => e.stopPropagation()}>
            <button className={styles.sheetClose} onClick={() => setNewTaskOpen(false)}>✕</button>
            <p className={styles.sheetLabel}>NUEVA TAREA</p>
            <p className={styles.sheetTitle}>¿Qué necesitas ejecutar?</p>
            <textarea className={styles.taskInput} placeholder="Describe tu tarea en una oración. El sistema la fragmentará automáticamente." rows={3} />
            <button className={styles.obBtn}>FRAGMENTAR Y ACTIVAR →</button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
